import React, { useState, useEffect, useRef } from "react";
import {
	ChevronDown,
	Menu,
	X,
	Zap,
	Phone,
	CreditCard,
	Users,
	Building2,
	HelpCircle,
	FileText,
	Shield,
	BarChart3,
	Settings,
	MapPin,
	Clock,
	AlertCircle,
	ArrowRight,
	Gauge,
	Image,
	Info,
	Scale,
	Flame,
	Award,
	Landmark,
	Construction,
	MessageSquare,
	ShieldCheck,
	ClipboardList,
	Plug,
	Wallet,
	SlidersHorizontal,
	RotateCcw,
	Sun,
	Moon,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";

/* ─── Types ─── */
interface DropdownItem {
	label: string;
	description?: string;
	icon?: React.ReactNode;
	href: string;
}

interface NavItem {
	label: string;
	href?: string;
	dropdown?: DropdownItem[];
}

/* ─── Navigation Data ─── */
const navItems: NavItem[] = [
	{
		label: "About Us",
		dropdown: [
			{ label: "Our Company", description: "History, mission & vision", icon: <Building2 size={18} />, href: "/about/our-company" },
			{ label: "Board of Directors", description: "Meet our board members", icon: <Users size={18} />, href: "/about/board-of-directors" },
			{ label: "Management Team", description: "Our leadership team", icon: <Users size={18} />, href: "/about/management-team" },
			{ label: "Area Offices", description: "Find an office near you", icon: <MapPin size={18} />, href: "/about/area-offices" },
			{ label: "QHSE Policy", description: "Quality, Health, Safety & Environment", icon: <Shield size={18} />, href: "/about/qhse-policy" },
			{ label: "Right of Way", description: "Power line right of way info", icon: <Scale size={18} />, href: "/about/right-of-way" },
		],
	},
	{
		label: "Metering",
		dropdown: [
			{ label: "MAP Process", description: "Step-by-step MAP meter application", icon: <SlidersHorizontal size={18} />, href: "/metering/map-process" },
			{ label: "New Connection", description: "Apply for a new electricity connection", icon: <Plug size={18} />, href: "/metering/new-customer-connection" },
			{ label: "MAP Prices", description: "Current meter asset provider prices", icon: <Wallet size={18} />, href: "/metering/map-prices" },
			{ label: "MAP Refund", description: "Apply for MAP meter cost refund", icon: <RotateCcw size={18} />, href: "https://kadelectric.ng/self-service/map-refund" },
			{ label: "Prepaid Meter Upgrade", description: "STS meter upgrade & TID rollover info", icon: <Zap size={18} />, href: "/metering/prepaid-meter-upgrade" },
		],
	},
	{
		label: "Customer Service",
		dropdown: [
			{ label: "Fire Safety Tips", description: "Essential electrical & fire safety", icon: <Flame size={18} />, href: "/customer/fire-safety-tips" },
			{ label: "Report Outage", description: "Notify us of power interruptions", icon: <AlertCircle size={18} />, href: "/customer/report-outage" },
			{ label: "Band Tariff", description: "Understand service reflective tariffs", icon: <BarChart3 size={18} />, href: "/customer/band-tariff" },
			{ label: "Payment Offices", description: "Cash & electronic payment points", icon: <CreditCard size={18} />, href: "/customer/payment-offices" },
			{ label: "Whistleblowing", description: "Report fraud & unethical conduct", icon: <ShieldCheck size={18} />, href: "/customer/whistleblowing" },
			{ label: "Log Complaints", description: "Submit feedback or service issues", icon: <MessageSquare size={18} />, href: "/customer/log-complaints" },
			{ label: "FAQs", description: "Frequently asked questions & answers", icon: <HelpCircle size={18} />, href: "/customer/faqs" },
			{ label: "Contact Us", description: "Hotline, email & headquarters", icon: <Phone size={18} />, href: "/customer/contact" },
		],
	},
	{
		label: "Media & Press",
		dropdown: [
			{ label: "News & Releases", description: "Corporate news & media updates", icon: <FileText size={18} />, href: "/news" },
			{ label: "Photo Gallery", description: "Photos from events & projects", icon: <Image size={18} />, href: "/media/gallery" },
		],
	},
	{
		label: "E-Payment Centers",
		href: "/e-payment-centers",
	},
];

/* ─── MegaMenuPanel Component ─── */
const MegaMenuPanel: React.FC<{
	items: DropdownItem[];
	isVisible: boolean;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
}> = ({ items, isVisible, onMouseEnter, onMouseLeave }) => {
	const { isDark } = useTheme();
	const gridCols =
		items.length <= 4
			? "grid-cols-2"
			: items.length <= 6
				? "grid-cols-3"
				: "grid-cols-4";

	return (
		<div
			className={`absolute left-0 right-0 top-full ${
				isDark
					? "bg-[#15151A] border-t border-white/[0.08]"
					: "bg-white border-t border-gray-100"
			} shadow-[0_20px_60px_rgba(0,0,0,0.15)] z-[999] transition-all duration-300 ease-out origin-top mega-menu-panel ${
				isVisible
					? "opacity-100 translate-y-0 scale-y-100 pointer-events-auto"
					: "opacity-0 -translate-y-2 scale-y-95 pointer-events-none"
			}`}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			{/* Invisible hover bridge */}
			<div
				className="absolute left-0 right-0 h-6 -top-6"
				style={{ pointerEvents: isVisible ? "auto" : "none" }}
				aria-hidden="true"
			/>
			<div className="max-w-[1280px] mx-auto px-4 md:px-8 py-8">
				<div className={`grid ${gridCols} gap-5`}>
					{items.map((sub) => (
						<a
							key={sub.label}
							href={sub.href}
							target={sub.href.startsWith("http") ? "_blank" : undefined}
							rel={sub.href.startsWith("http") ? "noopener noreferrer" : undefined}
							className={`group flex flex-row items-start gap-4 p-5 rounded-xl border no-underline transition-all duration-300 ${
								isDark
									? "bg-[#1C1C22] border-white/[0.06] hover:border-emerald-500/40 hover:bg-[#24242C]"
									: "bg-[#F7F8FA] border-gray-200 hover:border-gray-300 hover:bg-white hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)]"
							}`}
							role="menuitem"
						>
							{/* Icon */}
							<span
								className={`flex items-center justify-center w-11 h-11 rounded-lg transition-all duration-300 ${
									isDark
										? "bg-white/[0.06] text-emerald-400 group-hover:bg-emerald-600 group-hover:text-white group-hover:scale-105"
										: "bg-ke-gray-50 text-ke-gray-500 group-hover:bg-[#10B981] group-hover:text-white group-hover:scale-105 group-hover:shadow-[0_4px_12px_rgba(16,185,129,0.25)]"
								}`}
							>
								{sub.icon}
							</span>
							{/* Text + Arrow */}
							<div className="flex flex-col gap-1">
								<span
									className={`flex items-center gap-1.5 text-[0.95rem] font-bold transition-colors duration-300 ${
										isDark
											? "text-white group-hover:text-emerald-300"
											: "text-ke-navy-dark group-hover:text-[#10B981]"
									}`}
								>
									{sub.label}
									<ArrowRight
										size={14}
										className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-emerald-500"
									/>
								</span>
								{sub.description && (
									<span className={`text-[0.82rem] leading-[1.5] ${isDark ? "text-slate-400" : "text-ke-gray-400"}`}>
										{sub.description}
									</span>
								)}
							</div>
						</a>
					))}
				</div>
			</div>
		</div>
	);
};

/* ─── MobileMenu Component ─── */
const MobileMenu: React.FC<{
	isOpen: boolean;
	onClose: () => void;
}> = ({ isOpen, onClose }) => {
	const [expandedItem, setExpandedItem] = useState<string | null>(null);
	const { isDark, toggleTheme } = useTheme();

	return (
		<div
			id="mobile-menu-overlay"
			className={`fixed inset-0 z-[9999] bg-ke-navy-dark/50 backdrop-blur-[4px] ${isOpen ? "flex" : "hidden"}`}
		>
			<div className={`absolute top-0 right-0 w-[min(380px,90vw)] h-screen shadow-ke-xl flex flex-col overflow-y-auto ${
				isDark ? "bg-[#15151A] text-white" : "bg-white text-ke-navy"
			}`}>
				{/* Mobile Header */}
				<div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? "border-white/[0.08]" : "border-ke-gray-100"}`}>
					<img
						src="/static/images/kelogo.svg"
						alt="Kaduna Electric"
						className="h-9 w-auto object-contain"
					/>
					<button
						className={`flex items-center justify-center w-10 h-10 rounded-ke-sm border-none cursor-pointer transition-colors ${
							isDark
								? "bg-white/[0.06] text-white hover:bg-white/[0.12]"
								: "bg-ke-gray-50 text-ke-gray-500 hover:bg-ke-gray-100 hover:text-ke-navy"
						}`}
						onClick={onClose}
						aria-label="Close menu"
						id="btn-mobile-menu-close"
					>
						<X size={24} />
					</button>
				</div>

				{/* Mobile Navigation */}
				<nav className="flex-1 p-3">
					{navItems.map((item) => (
						<div key={item.label} className={`border-b ${isDark ? "border-white/[0.06]" : "border-ke-gray-100"}`}>
							{item.dropdown ? (
								<>
									<button
										className={`flex items-center justify-between w-full px-3 py-3.5 text-[0.95rem] font-semibold bg-transparent border-none cursor-pointer transition-colors ${
											isDark
												? "text-white hover:text-emerald-400"
												: "text-ke-navy hover:text-ke-navy-light"
										} mobile-nav-category`}
										onClick={(e) => {
											e.preventDefault();
											setExpandedItem(
												expandedItem === item.label ? null : item.label
											);
										}}
										data-category={item.label}
									>
										{item.label}
										<ChevronDown
											size={16}
											className={`opacity-50 transition-transform duration-[250ms] ease-in-out mobile-nav-chevron ${
												expandedItem === item.label ? "rotate-180" : ""
											}`}
										/>
									</button>
									<div
										className={`px-2 pb-3 flex flex-col gap-0.5 animate-slide-down mobile-nav-dropdown-content ${
											expandedItem === item.label ? "" : "hidden"
										}`}
										data-category-content={item.label}
									>
										{item.dropdown.map((sub) => (
											<a
												key={sub.label}
												href={sub.href}
												target={sub.href.startsWith("http") ? "_blank" : undefined}
												rel={sub.href.startsWith("http") ? "noopener noreferrer" : undefined}
												className={`flex items-center gap-3 px-3 py-2.5 rounded-ke-sm no-underline transition-colors ${
													isDark
														? "text-slate-300 hover:bg-white/[0.06] hover:text-white"
														: "text-ke-gray-500 hover:bg-ke-gray-50 hover:text-ke-navy"
												}`}
												onClick={(e) => {
													if (sub.href === "#" || !sub.href) {
														e.preventDefault();
													}
													onClose();
												}}
											>
												<span className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${
													isDark ? "bg-white/[0.06] text-emerald-400" : "bg-ke-gray-50 text-ke-gray-400"
												}`}>
													{sub.icon}
												</span>
												<div>
													<div className={`text-sm font-semibold ${isDark ? "text-white" : "text-ke-navy"}`}>
														{sub.label}
													</div>
													<div className="text-xs text-ke-gray-400">
														{sub.description}
													</div>
												</div>
											</a>
										))}
									</div>
								</>
							) : (
								<a
									href={item.href || "#"}
									className={`flex items-center justify-between w-full px-3 py-3.5 text-[0.95rem] font-semibold no-underline transition-colors ${
										isDark
											? "text-white hover:text-emerald-400"
											: "text-ke-navy hover:text-ke-navy-light"
									}`}
									onClick={(e) => {
										if (item.href === "#" || !item.href) {
											e.preventDefault();
										}
										onClose();
									}}
								>
									{item.label}
								</a>
							)}
						</div>
					))}

					{/* Theme Switcher in Mobile Drawer */}
					<div className="pt-6 px-2">
						<button
							onClick={toggleTheme}
							className={`w-full flex items-center justify-between p-3.5 rounded-xl border font-semibold text-[0.88rem] ${
								isDark
									? "bg-[#1C1C22] border-white/[0.08] text-white"
									: "bg-slate-50 border-slate-200 text-slate-800"
							}`}
						>
							<span className="flex items-center gap-2">
								{isDark ? <Sun size={17} className="text-amber-400" /> : <Moon size={17} className="text-emerald-600" />}
								<span>{isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}</span>
							</span>
							<span className="text-[0.72rem] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
								{isDark ? "Dark Active" : "Light Active"}
							</span>
						</button>
					</div>
				</nav>
			</div>
		</div>
	);
};

/* ─── Main Navbar Component ─── */
const Navbar: React.FC = () => {
	const [scrolled, setScrolled] = useState(false);
	const [openDropdown, setOpenDropdown] = useState<string | null>(null);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const { isDark, toggleTheme } = useTheme();

	const navRef = useRef<HTMLDivElement>(null);
	const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		let ticking = false;
		let lastState = window.scrollY > 20;
		setScrolled(lastState);

		const handleScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					const isScrolled = window.scrollY > 20;
					if (isScrolled !== lastState) {
						lastState = isScrolled;
						setScrolled(isScrolled);
					}
					ticking = false;
				});
				ticking = true;
			}
		};
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const handleMouseEnter = (label: string) => {
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		setOpenDropdown(label);
	};

	const handleMouseLeave = () => {
		closeTimeoutRef.current = setTimeout(() => {
			setOpenDropdown(null);
		}, 150);
	};

	const toggleDropdown = (label: string, e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (closeTimeoutRef.current) {
			clearTimeout(closeTimeoutRef.current);
			closeTimeoutRef.current = null;
		}
		setOpenDropdown(openDropdown === label ? null : label);
	};

	return (
		<>
			<style>{`
				@media (min-width: 768px) {
					.nav-item-container:hover .mega-menu-panel {
						opacity: 1 !important;
						transform: translateY(0) scaleY(1) !important;
						pointer-events: auto !important;
					}
					.nav-item-container:hover .nav-button {
						color: ${isDark ? "#34D399" : "var(--color-ke-navy)"} !important;
						background-color: ${isDark ? "rgba(255,255,255,0.06)" : "var(--color-ke-gray-50)"} !important;
					}
					.nav-item-container:hover .nav-chevron {
						transform: rotate(180deg) !important;
					}
				}
			`}</style>
			{/* ─── Top Bar ─── */}
			<div className={`hidden md:block ${
				isDark
					? "bg-[#0E0E0E] text-white/75 border-b border-white/[0.06]"
					: "bg-ke-navy-dark text-white/75 border-b border-white/[0.08]"
			} text-xs font-medium tracking-[0.01em]`}>
				<div className="max-w-[1280px] mx-auto px-8 py-2 flex justify-between items-center">
					<div className="flex items-center gap-3">
						<span className="inline-flex items-center gap-[5px]">
							<Phone size={12} className="text-emerald-400" /> 0905-KADELECTRIC (+234-905-554-0389)
						</span>
						<span className="opacity-30">|</span>
						<span className="inline-flex items-center gap-[5px]">
							<Clock size={12} className="text-emerald-400" /> Mon – Fri: 8AM – 5PM
						</span>
					</div>
					<div className="flex gap-5">
						<a
							href="/careers"
							className="text-white/75 no-underline transition-colors duration-[250ms] ease-in-out hover:text-ke-green-light"
							onClick={(e) => e.preventDefault()}
						>
							Careers
						</a>
						<a
							href="/tenders"
							className="text-white/75 no-underline transition-colors duration-[250ms] ease-in-out hover:text-ke-green-light"
							onClick={(e) => e.preventDefault()}
						>
							Tenders
						</a>
						<a
							href="/customer/whistleblowing"
							className="text-white/75 no-underline transition-colors duration-[250ms] ease-in-out hover:text-ke-green-light"
						>
							Whistleblower
						</a>
					</div>
				</div>
			</div>

			{/* ─── Header / Navbar ─── */}
			<header
				className={`sticky top-0 z-[1000] ${
					isDark
						? "bg-[#15151A]/95 backdrop-blur-[20px] border-b border-white/[0.08] shadow-lg shadow-black/20"
						: "bg-white/95 backdrop-blur-[20px] border-b border-ke-navy/[0.06]"
				} transition-[box-shadow,background-color,border-color] duration-200 ease-in-out ${
					scrolled ? (isDark ? "shadow-xl border-b-transparent" : "shadow-ke-md border-b-transparent") : ""
				}`}
				id="main-header"
			>
				<div className="max-w-[1280px] mx-auto px-4 md:px-8 h-16 md:h-[72px] flex items-center justify-between">
					{/* Logo */}
					<a
						href="/"
						className="flex items-center no-underline shrink-0"
					>
						<img
							src="/static/images/kelogo.svg"
							alt="Kaduna Electric"
							className="h-[42px] w-auto object-contain transition-all"
						/>
					</a>

					{/* Desktop Nav */}
					<nav
						ref={navRef}
						className="hidden md:flex items-center gap-1 ml-10 h-full"
						id="main-nav"
						role="navigation"
						aria-label="Main navigation"
					>
						{navItems.map((item) =>
							item.dropdown ? (
								<div
									key={item.label}
									onMouseEnter={() => handleMouseEnter(item.label)}
									onMouseLeave={handleMouseLeave}
									className="h-full flex items-center nav-item-container"
								>
									<button
										className={`inline-flex items-center gap-1 px-4 py-2 text-[0.9rem] font-medium rounded-ke-sm cursor-pointer whitespace-nowrap transition-all duration-[250ms] ease-in-out border-none bg-transparent nav-button ${
											openDropdown === item.label
												? isDark ? "text-emerald-300 bg-white/[0.08]" : "text-ke-navy bg-ke-gray-50"
												: isDark ? "text-slate-300 hover:text-white hover:bg-white/[0.06]" : "text-ke-gray-600 hover:text-ke-navy hover:bg-ke-gray-50"
										}`}
										aria-expanded={openDropdown === item.label}
										aria-haspopup="true"
										id={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
										onClick={(e) => toggleDropdown(item.label, e)}
									>
										{item.label}
										<ChevronDown
											size={14}
											className={`opacity-50 transition-transform duration-[250ms] ease-in-out nav-chevron ${
												openDropdown === item.label ? "rotate-180" : ""
											}`}
										/>
									</button>
									<MegaMenuPanel
										items={item.dropdown}
										isVisible={openDropdown === item.label}
										onMouseEnter={() => handleMouseEnter(item.label)}
										onMouseLeave={handleMouseLeave}
									/>
								</div>
							) : (
								<a
									key={item.label}
									href={item.href || "#"}
									className={`inline-flex items-center px-4 py-2 text-[0.9rem] font-medium rounded-ke-sm whitespace-nowrap transition-all duration-[250ms] ease-in-out no-underline ${
										isDark
											? "text-slate-300 hover:text-white hover:bg-white/[0.06]"
											: "text-ke-gray-600 hover:text-ke-navy hover:bg-ke-gray-50"
									}`}
									onMouseEnter={() => setOpenDropdown(null)}
									onClick={(e) => {
										if (item.href === "#" || !item.href) {
											e.preventDefault();
										}
									}}
								>
									{item.label}
								</a>
							)
						)}
					</nav>

					{/* CTA & Theme Tools */}
					<div className="hidden md:flex items-center gap-3">
						{/* Animated Theme Switcher Button */}
						<button
							onClick={toggleTheme}
							aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
							className={`w-10 h-10 rounded-full flex items-center justify-center border cursor-pointer transition-all duration-300 ${
								isDark
									? "bg-white/[0.06] border-white/10 text-amber-400 hover:bg-white/[0.12] hover:scale-105"
									: "bg-ke-gray-50 border-ke-gray-200 text-slate-700 hover:bg-ke-gray-100 hover:scale-105"
							}`}
						>
							{isDark ? (
								<Sun size={18} className="rotate-0 hover:rotate-45 transition-transform duration-300" />
							) : (
								<Moon size={18} className="rotate-0 hover:-rotate-12 transition-transform duration-300" />
							)}
						</button>

						{/* Customer Login Button */}
						<a
							href="https://portal.kadelectric.ng/customer-portal/auth"
							style={{ background: isDark ? "linear-gradient(180deg, #10B981 0%, #059669 100%)" : undefined }}
							className={`inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white no-underline rounded-ke-sm transition-all duration-[250ms] ease-in-out ${
								isDark
									? "shadow-[0_4px_14px_rgba(16,185,129,0.35)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.5)] hover:opacity-95 hover:-translate-y-px"
									: "bg-ke-navy shadow-[0_2px_8px_rgba(15,90,65,0.25)] hover:bg-ke-navy-light hover:-translate-y-px hover:shadow-[0_4px_16px_rgba(15,90,65,0.3)]"
							}`}
							id="btn-get-started"
							target="_blank"
							rel="noopener noreferrer"
						>
							Customer Login
						</a>
					</div>

					{/* Mobile toggle */}
					<div className="flex md:hidden items-center gap-2">
						<button
							onClick={toggleTheme}
							aria-label="Toggle theme"
							className={`w-9 h-9 rounded-full flex items-center justify-center border ${
								isDark ? "bg-white/[0.06] border-white/10 text-amber-400" : "bg-slate-100 border-slate-200 text-slate-700"
							}`}
						>
							{isDark ? <Sun size={16} /> : <Moon size={16} />}
						</button>
						<button
							className={`flex items-center justify-center w-11 h-11 border rounded-ke-sm cursor-pointer transition-all duration-[250ms] ease-in-out ${
								isDark
									? "bg-transparent border-white/10 text-white hover:bg-white/[0.06]"
									: "bg-transparent border-ke-gray-200 text-ke-navy hover:bg-ke-gray-50"
							}`}
							onClick={() => setMobileMenuOpen(true)}
							aria-label="Open menu"
							id="btn-mobile-menu"
						>
							<Menu size={24} />
						</button>
					</div>
				</div>
			</header>

			{/* Mobile menu */}
			<MobileMenu
				isOpen={mobileMenuOpen}
				onClose={() => setMobileMenuOpen(false)}
			/>
		</>
	);
};

export default React.memo(Navbar);
